import { coerceDeliveryStage } from '@/components/deal/vehicle/delivery-stage/delivery-stage-logic';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ConfirmationDot from '@/components/ui/verification/confirmation-dot.tsx';
import useDeal from '@/hooks/use-deal';
import { centsToInitialValue } from '@/lib/currency';
import type { AppraisalV2, Deal, ProtectionPlan } from '@/models/registry';
import { VehicleListing } from '@/models/registry';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types.ts';
import { observable } from 'mobx';
import TradeInListMobile from '../../trade/trade-in-list-mobile';
import { ProtectionPlanBadge } from '../protection-plan/protection-plan-badge';
import { DeliveryStageSelector } from './delivery-stage/delivery-stage-selector';
import { RapidReconStepSelector } from './rapid-recon-step/rapid-recon-step-selector';
import VehicleLocationSelector from './vehicle-location-selector';
import VehicleMileageEditor from './vehicle-mileage-editor';

const DealVehicleEditor = ({
  vehicle,
  dealId,
  dealShortId,
  protectionPlan,
  priceBreakdown,
  rapidReconStep,
  vehicleDeliveryOdometer,
  vehicleCurrentLocation,
  appraisals,
}: {
  vehicle: VehicleListing;
  dealId: Deal['id'];
  dealShortId: Deal['short_id'];
  protectionPlan: ProtectionPlan | null;
  priceBreakdown: Deal['price_breakdown'];
  rapidReconStep: Deal['rapid_recon_step'];
  deliveryStage: Deal['delivery_stage'];
  vehicleDeliveryOdometer: Deal['vehicle_delivery_odometer'];
  vehicleCurrentLocation: Deal['vehicle_current_location'];
  appraisals?: AppraisalV2[];
}) => {
  const taxesAndFeedsIncluded = priceBreakdown?.sales_tax !== 0;
  const { deal } = useDeal(dealId);
  const dealStatus = deal?.status ?? 'new';
  const coercedDeliveryStage = coerceDeliveryStage(
    dealStatus,
    deal?.delivery_stage ?? undefined,
  );

  return (
    <div className="flex flex-col gap-1 dark:bg-dark dark:text-white">
      <div className="flex flex-col w-full gap-1 justify-between flex-wrap">
        <div className="grid grid-cols-[1fr_auto_24px] gap-1 items-center w-full">
          <div className="text-sm text-uneditable text-nowrap overflow-hidden text-ellipsis !dark:text-white">
            <Tooltip delayDuration={500}>
              <TooltipTrigger asChild>
                <span>YMMT</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Year, Make, Model, Trim</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <a
            href={`https://paddock.evercars.com/cars/${vehicle.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-uneditable text-nowrap overflow-hidden text-ellipsis hover:underline text-right"
          >
            {vehicle.YMMT} ($
            {centsToInitialValue(priceBreakdown?.subtotal || 0)})
          </a>
        </div>

        <div className="grid grid-cols-[1fr_auto_24px] gap-1 items-center w-full">
          <div className="text-sm text-uneditable">Price</div>
          <div className="text-sm text-uneditable text-right">
            ${centsToInitialValue(priceBreakdown?.total || 0)} (
            {taxesAndFeedsIncluded ? `incl. taxes & fees` : 'w/o taxes & fees'})
          </div>
        </div>

        <div className="grid grid-cols-[1fr_auto_24px] gap-1 items-center w-full">
          <div className="text-sm text-uneditable">VIN</div>
          <div className="text-sm text-uneditable text-right">
            {vehicle.vehicle?.vin}
          </div>
          <ConfirmationDot
            targetId={dealId}
            targetType={ConfirmationTargetType.Deal}
            field={ConfirmationField.Vin}
            value={observable.box(vehicle.vehicle?.vin ?? '')}
          />
        </div>

        <TradeInListMobile appraisals={appraisals} />
      </div>

      <VehicleMileageEditor
        initialMileage={vehicleDeliveryOdometer || 0}
        dealId={dealId}
      />

      <div className="grid grid-cols-[1fr_auto_24px] gap-1 items-center w-full">
        <div className="text-sm text-uneditable">Current Location</div>
        <VehicleLocationSelector
          dealId={dealId}
          dealShortId={dealShortId}
          vehicleCurrentLocationId={vehicleCurrentLocation?.id ?? null}
        />
        <ConfirmationDot
          targetId={dealId}
          targetType={ConfirmationTargetType.Deal}
          field={ConfirmationField.VehicleCurrentLocation}
          value={observable.box(vehicleCurrentLocation?.name ?? '')}
        />
      </div>

      <div className="flex flex-col gap-1">
        <div className="grid grid-cols-[1fr_auto_24px] gap-1 items-center w-full">
          <p className="text-sm text-uneditable">Rapid Recon</p>
          <RapidReconStepSelector
            id={dealId}
            shortId={dealShortId}
            value={rapidReconStep || 'None'}
            badgeClassName="px-0"
            withHotkeyListener
          />
          <ConfirmationDot
            targetId={dealId}
            targetType={ConfirmationTargetType.Deal}
            field={ConfirmationField.RapidReconStatus}
            value={observable.box(rapidReconStep || 'None')}
          />
        </div>

        <div className="flex flex-col w-full gap-1 justify-between flex-wrap">
          <div className="grid grid-cols-[1fr_auto_24px] gap-1 items-center w-full">
            <div className="text-sm text-uneditable text-nowrap overflow-hidden text-ellipsis !dark:text-white">
              Protection Plan
            </div>
            <ProtectionPlanBadge
              dealId={dealId}
              protectionPlan={protectionPlan}
            />
          </div>

          <div className="grid grid-cols-[1fr_auto_24px] gap-1 items-center w-full">
            <p className="text-sm text-uneditable">Delivery Stage</p>
            <DeliveryStageSelector
              id={dealId}
              shortId={dealShortId}
              dealStatus={dealStatus}
              value={coercedDeliveryStage}
              badgeClassName="px-0"
              withHotkeyListener
            />
            <ConfirmationDot
              targetId={dealId}
              targetType={ConfirmationTargetType.Deal}
              field={ConfirmationField.DeliveryStage}
              value={observable.box(coercedDeliveryStage)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealVehicleEditor;
